/**
 * Tariff Classifier Page Service
 * API service for file upload and batch classification
 */

import api from '../shared/services/api';

export interface DirectClassificationRequest {
  product_description: string;
  materials?: string;
  origin_country?: string;
  intended_use?: string;
  language?: string;
}

/** One rung of the 2→4→6→8 prediction ladder. */
export interface TariffHierarchyLevel {
  level: 'chapter' | 'heading' | 'subheading' | 'cn8';
  digits: number;               // 2 / 4 / 6 / 8
  code: string;                 // the prefix chosen at this level
  title: string | null;         // human-readable name (may be null if unknown)
  confidence: number;           // 0–1 share of candidate mass under this prefix
  alternatives: Array<{ code: string; title: string | null; confidence: number }>;
}

export interface TariffHierarchy {
  selected_code: string;
  levels: TariffHierarchyLevel[];
}

export interface DirectClassificationResponse {
  transaction_id: string;
  original_description: string;
  generated_hs_code: string;
  confidence: number;
  reasoning: string;
  status: string;
  processing_time_ms: number;
  rule_applied?: boolean;
  rule_name?: string | null;
  hierarchy?: TariffHierarchy | null;
}

export interface FileUploadResponse {
  transaction_id: string;
  file_name: string;
  file_type: string;
  total_items: number;
  status: string;
}

export interface ClassificationResult {
  row_number: number;
  original_description: string;
  original_hs_code?: string | null;
  generated_hs_code: string;
  confidence: number;
  reasoning?: string;
  status: string;
}

export interface ClassificationResultsResponse {
  transaction_id: string;
  status: string;
  total_items: number;
  successful_items: number;
  failed_items: number;
  average_confidence: number;
  processing_time_ms: number;
  results: ClassificationResult[];
  output_file_name?: string | null;
}

export interface ClassificationProgressResponse {
  transaction_id: string;
  processed: number;
  total: number;
  status: string;
}

export interface TransactionHistory {
  id: string;
  classification_mode: string;
  uploaded_file_name?: string | null;
  uploaded_file_type?: string | null;
  total_items?: number | null;
  successful_items?: number | null;
  failed_items?: number | null;
  average_confidence?: number | null;
  status: string;
  created_at: string;
  completed_at?: string | null;
  input_data?: any;  // Direct classification input
  batch_input_data?: any[];  // File upload batch input
  output_data?: any;  // Classification results
  processing_time_ms?: number | null;
  // Groups transactions from the same bulk upload (null for single-file flows)
  bulk_transaction_id?: string | null;
}

/**
 * Classify single product directly
 */
export const classifyDirect = async (
  request: DirectClassificationRequest
): Promise<DirectClassificationResponse> => {
  const response = await api.post('/api/v1/tariff-classifier-page/classify-direct', request, {
    timeout: 60000,
  });
  return response.data;
};

/**
 * Helper function to convert file to base64
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:image/jpeg;base64, prefix
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Upload file for batch classification (using base64 JSON approach)
 */
export const uploadFile = async (file: File): Promise<FileUploadResponse> => {
  console.log('[Service] uploadFile called with file:', file.name, file.size, 'bytes');
  console.log('[Service] Converting file to base64...');

  try {
    // Convert file to base64
    const base64Content = await fileToBase64(file);

    // Create JSON payload
    const uploadData = {
      filename: file.name,
      content: base64Content,
      content_type: file.type
    };

    console.log('[Service] Base64 conversion complete, making POST request to /api/v1/tariff-classifier-page/upload-file');
    console.log('[Service] Payload size:', JSON.stringify(uploadData).length, 'characters');

    const response = await api.post('/api/v1/tariff-classifier-page/upload-file', uploadData, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 120000,
    });

    console.log('[Service] Upload successful, response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[Service] Upload failed:', error);
    throw error;
  }
};

/**
 * Upload and classify a document (PDF, Image) for a single product directly
 */
export const classifyDocument = async (
  file: File,
  mode: string = 'quick',
  description?: string
): Promise<ClassificationResultsResponse> => {
  console.log('[Service] classifyDocument called with file:', file.name, file.size, 'bytes');

  try {
    const base64Content = await fileToBase64(file);

    const uploadData = {
      filename: file.name,
      content: base64Content,
      content_type: file.type,
      mode: mode,
      description: description,
    };

    const response = await api.post('/api/v1/tariff-classifier-page/classify-document', uploadData, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 180000,
    });

    console.log('[Service] Document classification successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('[Service] Document classification failed:', error);
    throw error;
  }
};

// ============================================================================
// Column Mapping (Preview Upload)
// ============================================================================

export interface FilePreviewResponse {
  transaction_id: string;
  file_name: string;
  file_type: string;
  columns: string[];
  preview_rows: Record<string, string | null>[];
  suggested_description_col: string | null;
  suggested_hs_code_col: string | null;
  total_rows: number;
  status: string;
}

/**
 * Upload file and get column preview for mapping (CSV/Excel only)
 */
export const uploadFilePreview = async (file: File): Promise<FilePreviewResponse> => {
  console.log('[Service] uploadFilePreview called with file:', file.name);

  try {
    const base64Content = await fileToBase64(file);

    const response = await api.post('/api/v1/tariff-classifier-page/upload-file-preview', {
      filename: file.name,
      content: base64Content,
      content_type: file.type
    }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 60000,
    });

    console.log('[Service] File preview ready:', response.data.columns.length, 'columns');
    return response.data;
  } catch (error) {
    console.error('[Service] File preview failed:', error);
    throw error;
  }
};

/**
 * Re-fetch the column preview for a transaction that's stuck in
 * 'awaiting_mapping' (e.g. after the user closed the page mid-bulk-upload).
 */
export const getMappingPreview = async (
  transactionId: string
): Promise<FilePreviewResponse> => {
  const response = await api.get(
    `/api/v1/tariff-classifier-page/transaction/${transactionId}/mapping-preview`,
    { timeout: 30000 }
  );
  return response.data;
};

/**
 * Confirm column mapping and start processing
 */
export const confirmColumnMapping = async (
  transactionId: string,
  descriptionColumn: string,
  hsCodeColumn?: string
): Promise<FileUploadResponse> => {
  console.log('[Service] confirmColumnMapping:', transactionId, descriptionColumn);

  try {
    const response = await api.post(`/api/v1/tariff-classifier-page/confirm-mapping/${transactionId}`, {
      description_column: descriptionColumn,
      hs_code_column: hsCodeColumn || null,
    }, {
      timeout: 120000,
    });

    console.log('[Service] Column mapping confirmed:', response.data);
    return response.data;
  } catch (error) {
    console.error('[Service] Column mapping failed:', error);
    throw error;
  }
};

// ============================================================================
// Bulk Upload
// ============================================================================

export interface BulkFileInfo {
  filename: string;
  file_type: string;
  items_extracted: number;
  transaction_id: string;
  status: string;
  error?: string;
  // Set when the backend couldn't auto-detect a description column.
  // The frontend must collect a column mapping from the user before
  // this file can be classified.
  needs_mapping?: boolean;
  columns?: string[];
  preview_rows?: Record<string, string | null>[];
  suggested_description_col?: string | null;
  suggested_hs_code_col?: string | null;
  total_rows?: number;
  // Set for completed PDF/image files so the frontend can render inline cards
  // without a follow-up /transaction/{id} fetch.
  output_data?: ClassificationResult[];
}

export interface BulkUploadResponse {
  bulk_transaction_id: string;
  total_files: number;
  successful_files: number;
  failed_files: number;
  total_items: number;
  file_details: BulkFileInfo[];
  status: string;
}

export interface BulkUploadItem {
  file: File;
  // Optional per-file description (used by PDF/image files only; CSV/Excel ignore it)
  description?: string;
}

/**
 * Upload multiple files for batch classification (parallel processing).
 * Supports a mix of tabular files (CSV/Excel) and unstructured files (PDF/JPG/PNG).
 * The backend classifies unstructured files synchronously; tabular files go through
 * the usual extract → mapping (if needed) → classify-batch pipeline.
 */
export const uploadFilesBulk = async (
  items: Array<BulkUploadItem | File>,
  mode: 'quick' | 'advanced' = 'quick'
): Promise<BulkUploadResponse> => {
  // Accept legacy plain-File[] callers as well as the new {file, description} shape
  const normalized: BulkUploadItem[] = items.map((it) =>
    it instanceof File ? { file: it } : it
  );

  console.log('[Service] uploadFilesBulk called with', normalized.length, 'files, mode:', mode);

  try {
    const filesData = await Promise.all(
      normalized.map(async (item) => ({
        filename: item.file.name,
        content: await fileToBase64(item.file),
        content_type: item.file.type,
        description: item.description,
      }))
    );

    const response = await api.post('/api/v1/tariff-classifier-page/upload-files-bulk', {
      files: filesData,
      mode,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutes for bulk uploads
    });

    console.log('[Service] Bulk upload successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('[Service] Bulk upload failed:', error);
    throw error;
  }
};

/**
 * Process batch classification
 * @param mode - "quick" (default) | "advanced"
 */
export const classifyBatch = async (
  transactionId: string,
  mode: 'quick' | 'advanced' = 'quick'
): Promise<ClassificationResultsResponse> => {
  const response = await api.post(
    `/api/v1/tariff-classifier-page/classify-batch/${transactionId}?mode=${mode}`,
    {},
    {
      timeout: 1200000, // 20 minutes for large batches / Advanced Reasoning
    }
  );
  return response.data;
};

/**
 * Get real-time progress of batch classification
 */
export const getBatchProgress = async (
  transactionId: string
): Promise<ClassificationProgressResponse> => {
  const response = await api.get(
    `/api/v1/tariff-classifier-page/progress/${transactionId}`
  );
  return response.data;
};

/**
 * Download results as CSV or Excel
 */
export const downloadResults = async (
  transactionId: string,
  format: 'csv' | 'excel' = 'csv'
): Promise<Blob> => {
  const response = await api.get(
    `/api/v1/tariff-classifier-page/download/${transactionId}`,
    {
      params: { format },
      responseType: 'blob',
    }
  );
  return response.data;
};

/**
 * Download multiple results as a single ZIP file (one CSV per transaction)
 */
export const downloadBulkResults = async (
  transactionIds: string[]
): Promise<Blob> => {
  const response = await api.post(
    '/api/v1/tariff-classifier-page/download-bulk',
    { transaction_ids: transactionIds },
    {
      responseType: 'blob',
      timeout: 120000, // 2 minutes for generating ZIP
    }
  );
  return response.data;
};

/**
 * Download multiple results as a single merged CSV or Excel file.
 * All rows from every transaction are concatenated with a leading
 * `source_file` column so each row traces back to its origin upload.
 */
export const downloadBulkMerged = async (
  transactionIds: string[],
  format: 'csv' | 'excel' = 'csv'
): Promise<Blob> => {
  const response = await api.post(
    '/api/v1/tariff-classifier-page/download-bulk-merged',
    { transaction_ids: transactionIds, format },
    {
      responseType: 'blob',
      timeout: 120000,
    }
  );
  return response.data;
};

/**
 * Get transaction history
 */
export const getHistory = async (
  skip: number = 0,
  limit: number = 50
): Promise<TransactionHistory[]> => {
  const response = await api.get('/api/v1/tariff-classifier-page/history', {
    params: { skip, limit },
  });
  return response.data;
};

/**
 * Get transaction history for a specific user (Admin only)
 */
export const getUserHistory = async (
  userId: string,
  skip: number = 0,
  limit: number = 50
): Promise<TransactionHistory[]> => {
  const response = await api.get(`/api/v1/admin/tariff-classifier/history/${userId}`, {
    params: { skip, limit },
  });
  return response.data;
};

/**
 * Get all users' tariff classification history (Admin only)
 */
export const getAdminHistory = async (
  skip: number = 0,
  limit: number = 50
): Promise<(TransactionHistory & { user_id?: string; username?: string })[]> => {
  const response = await api.get('/api/v1/admin/tariff-classifier/history', {
    params: { skip, limit },
  });
  return response.data;
};

/**
 * Get transaction details
 */
export const getTransactionDetails = async (transactionId: string): Promise<any> => {
  const response = await api.get(
    `/api/v1/tariff-classifier-page/transaction/${transactionId}`
  );
  return response.data;
};

/**
 * Helper: Trigger file download
 */
export const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download original uploaded file
 */
export const downloadOriginalFile = async (transactionId: string): Promise<Blob> => {
  const response = await api.get(
    `/api/v1/tariff-classifier-page/original-file/${transactionId}`,
    {
      responseType: 'blob',
    }
  );
  return response.data;
};

// ============================================================================
// Advanced RAG Pipeline
// ============================================================================

export interface AdvancedClassificationRequest {
  product_description: string;
  materials?: string;
  origin_country?: string;
  intended_use?: string;
  return_alternatives?: boolean;
  language?: string;
  /** Pipeline mode:
   *  'quick'    – FAISS + local reranker, no LLM (default)
   *  'advanced' – FAISS + Mistral LLM judge picks the best match
   */
  mode?: 'quick' | 'advanced';
}

export interface AdvancedClassificationResponse {
  hs_code: string;
  confidence: number;
  description: string;
  reasoning: string;
  chapter: string;
  chapter_name: string;
  alternatives: any[];
  rag_details?: {
    path_taken: string;
    needs_human_review?: boolean;
  };
  hierarchy?: TariffHierarchy | null;
}

export interface LegalChild {
  code: string;
  title: string | null;
}

/** Valid child codes of a 2/4/6-digit prefix (for the correction cascade). */
export const getLegalChildren = async (
  prefix: string,
  language: 'en' | 'de' = 'en'
): Promise<LegalChild[]> => {
  const response = await api.get(
    `/api/v1/tariff-classifier/legal-children/${prefix}`,
    { params: { language } }
  );
  return response.data.children ?? [];
};

/** All 2-digit chapters (top level has no parent to enumerate via legal-children). */
export const getChapters = async (
  language: 'en' | 'de' = 'en'
): Promise<LegalChild[]> => {
  const response = await api.get('/api/v1/tariff-classifier/chapters', {
    params: { language },
  });
  return response.data.children ?? [];
};

export interface TariffFeedbackPayload {
  product_description: string;
  correct_hs_code: string;
  correction_level: 'chapter' | 'heading' | 'subheading' | 'cn8';
  suggested_hs_code?: string;
  transaction_id?: string;
  user_note?: string;
}

/** Submit a per-level HS code correction; applied to the live index immediately. */
export const submitTariffFeedback = async (
  payload: TariffFeedbackPayload
): Promise<{ id: string; status: string; correction_level: string }> => {
  const response = await api.post('/api/v1/tariff-classifier/feedback', payload);
  return response.data;
};

/**
 * Classify using the new RAG Pipeline (Advanced)
 */
export const classifyAdvanced = async (
  request: AdvancedClassificationRequest
): Promise<AdvancedClassificationResponse> => {
  const response = await api.post('/api/v1/tariff-classifier/classify-advanced', request, {
    timeout: 180000, // Increased to 3 minutes for initial model loading
  });
  return response.data;
};

export default {
  classifyDirect,
  uploadFile,
  classifyDocument,
  uploadFilesBulk,
  classifyBatch,
  getBatchProgress,
  downloadResults,
  getHistory,
  getUserHistory,
  getTransactionDetails,
  triggerDownload,
  classifyAdvanced,
  getLegalChildren,
  getChapters,
  submitTariffFeedback,
};
